import { Toast } from "../../submodules/rapdv/client/elements/Bootstrap"
import { SocketClient } from "../socket/SocketClient"
import { SocketEvents } from "../../server/socket/SocketEvents"
import { RefundFailureReason } from "../../server/Types"
import { Translation } from "./Translation"
import { SoundAlarm } from "./SoundAlarm"

export class OrderToast {

  private static CONTAINER_ID = "order-toast-container"
  private static CONTAINER_STYLE = "position:fixed;top:1rem;right:1rem;z-index:1100;min-width:280px;"
  private static AUTOHIDE_DELAY_MS = 10000
  private static TRANSLATION_ID = "OrderToast"

  private static translation = new Translation()

  private static SOUND_VOLUME = 0.9
  private static SOUND_FADE_S = 0.02
  // A short two-tone ascending chime so a new order is recognisable by ear.
  private static SOUND_TONES = [
    { frequency: 880, startOffset: 0, duration: 0.15 },
    { frequency: 1320, startOffset: 0.18, duration: 0.2 },
  ]
  private static audioContext: AudioContext | null = null

  public static initialize = (): void => {
    SocketClient.on(SocketEvents.OrderNew, OrderToast.show)
    SocketClient.on(SocketEvents.OrderRefundFailed, OrderToast.showRefundFailed)
    OrderToast.translation.fetchTranslations(OrderToast.TRANSLATION_ID)
    OrderToast.primeAudioOnFirstGesture()
  }

  // Browsers block audio until the user interacts with the page, so resume the context on the first
  // gesture. By the time an order arrives the chime can then play without a user gesture of its own.
  private static primeAudioOnFirstGesture = (): void => {
    const prime = (): void => {
      void OrderToast.getAudioContext()?.resume()
      window.removeEventListener("pointerdown", prime)
      window.removeEventListener("keydown", prime)
    }
    window.addEventListener("pointerdown", prime)
    window.addEventListener("keydown", prime)
  }

  private static getAudioContext = (): AudioContext | null => {
    try {
      const AudioContextClass = window.AudioContext ?? (window as any).webkitAudioContext
      if (!AudioContextClass) return null
      if (!OrderToast.audioContext) OrderToast.audioContext = new AudioContextClass()
      return OrderToast.audioContext
    } catch (error) {
      return null
    }
  }

  private static playSound = (): void => {
    if (!SoundAlarm.isEnabled()) return
    try {
      const ctx = OrderToast.getAudioContext()
      if (!ctx) return
      if (ctx.state === "suspended") void ctx.resume()

      for (const tone of OrderToast.SOUND_TONES) {
        OrderToast.playTone(ctx, tone.frequency, ctx.currentTime + tone.startOffset, tone.duration)
      }
    } catch (error) {
      // Best-effort: never let a missing/blocked audio device disrupt the page.
    }
  }

  private static playTone = (ctx: AudioContext, frequency: number, startTime: number, duration: number): void => {
    const oscillator = ctx.createOscillator()
    const gain = ctx.createGain()
    oscillator.type = "sine"
    oscillator.frequency.value = frequency
    // Ramp the volume up/down so the tone doesn't click on start/stop.
    gain.gain.setValueAtTime(0, startTime)
    gain.gain.linearRampToValueAtTime(OrderToast.SOUND_VOLUME, startTime + OrderToast.SOUND_FADE_S)
    gain.gain.linearRampToValueAtTime(0, startTime + duration)
    oscillator.connect(gain)
    gain.connect(ctx.destination)
    oscillator.start(startTime)
    oscillator.stop(startTime + duration)
  }

  private static getOrCreateContainer = (): HTMLElement => {
    let container = document.getElementById(OrderToast.CONTAINER_ID)
    if (!container) {
      container = document.createElement("div")
      container.id = OrderToast.CONTAINER_ID
      container.style.cssText = OrderToast.CONTAINER_STYLE
      document.body.appendChild(container)
    }
    return container
  }

  private static show = async (data: {
    restaurantKey: string
    orderId: string
  }): Promise<void> => {
    if (!data?.restaurantKey) return

    OrderToast.playSound()

    const t = await OrderToast.getTranslator()
    await OrderToast.renderToast({
      iconClass: "bi bi-bell-fill text-warning",
      title: t("New order"),
      message: t("A new order has been received."),
      restaurantKey: data.restaurantKey,
      orderId: data.orderId,
      autohide: true,
    })
  }

  private static showRefundFailed = async (data: {
    restaurantKey: string
    orderId: string
    reason: string
  }): Promise<void> => {
    if (!data?.restaurantKey) return

    const t = await OrderToast.getTranslator()
    const message = data.reason === RefundFailureReason.InsufficientFunds
      ? t("Your Stripe balance doesn't have enough available funds to refund this order. Recent payments may still be settling. Try again later.")
      : t("The refund couldn't be processed. Please try again later.")
    await OrderToast.renderToast({
      iconClass: "bi bi-exclamation-triangle-fill text-danger",
      title: t("Refund failed"),
      message,
      restaurantKey: data.restaurantKey,
      orderId: data.orderId,
      autohide: false,
    })
  }

  private static getTranslator = async (): Promise<(key: string) => string> => {
    if (OrderToast.translation.isEmpty()) {
      await OrderToast.translation.fetchTranslations(OrderToast.TRANSLATION_ID)
    }
    return OrderToast.translation.t
  }

  private static renderToast = async (params: {
    iconClass: string
    title: string
    message: string
    restaurantKey: string
    orderId: string
    autohide: boolean
  }): Promise<void> => {
    const t = await OrderToast.getTranslator()
    const container = OrderToast.getOrCreateContainer()
    const ordersUrl = `/${Translation.getLocationCode()}/orders/${params.restaurantKey}?page=1&orderId=${params.orderId}`

    const toastEl = document.createElement("div")
    toastEl.className = "toast"
    toastEl.setAttribute("role", "alert")
    toastEl.setAttribute("aria-live", "assertive")
    toastEl.setAttribute("aria-atomic", "true")
    toastEl.innerHTML = `
      <div class="toast-header">
        <i class="${params.iconClass} me-2"></i>
        <strong class="me-auto">${params.title}</strong>
        <button type="button" class="btn-close" data-bs-dismiss="toast" aria-label="${t("Close")}"></button>
      </div>
      <div class="toast-body">
        ${params.message}
        <div class="mt-2">
          <a href="${ordersUrl}" class="btn btn-sm btn-primary">${t("View order")}</a>
        </div>
      </div>
    `

    container.appendChild(toastEl)
    const toast = new Toast(toastEl, { autohide: params.autohide, delay: OrderToast.AUTOHIDE_DELAY_MS })
    toast.show()
    toastEl.addEventListener("hidden.bs.toast", () => toastEl.remove())
  }
}
